import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Clock } from 'lucide-react';
import { Button } from '../components/design-system';
import { useAuth, hasStoredUserToken } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Destino del enlace de acceso que mandamos por correo (/entrar/:token)
const LoginLink = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, expired, invalid, error
  // El enlace es de un solo uso: evitar que un doble montaje (StrictMode) lo gaste
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    if (requestedRef.current) return;
    requestedRef.current = true;

    const verify = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/login-link/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await response.json();

        if (response.ok && data.valid && data.token && data.user) {
          setSession(data.token, data.user);
          localStorage.setItem('alphatica_registered', 'true');
          setStatus('success');
          setTimeout(() => navigate('/profile', { replace: true }), 1200);
        } else if (hasStoredUserToken()) {
          // Enlace ya usado/vencido pero este dispositivo ya tiene sesión: no asustar con un error
          navigate('/profile', { replace: true });
        } else if (data.reason === 'expired_token') {
          setStatus('expired');
        } else {
          setStatus('invalid');
        }
      } catch (err) {
        setStatus('error');
      }
    };

    verify();
  }, [token, setSession, navigate]);

  if (status === 'loading' || status === 'success') {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid={`login-link-${status}`}>
        {status === 'loading' ? (
          <Loader2 className="w-16 h-16 mx-auto mb-6 text-brand-cobalt animate-spin" />
        ) : (
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
        )}
        <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
          {status === 'loading' ? 'Entrando a tu cuenta...' : '¡Listo, ya entraste!'}
        </h1>
      </div>
    );
  }

  const expired = status === 'expired';
  return (
    <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid={`login-link-${status}`}>
      <div className={`w-20 h-20 ${expired ? 'bg-amber-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
        {expired ? <Clock className="w-10 h-10 text-amber-600" /> : <XCircle className="w-10 h-10 text-red-600" />}
      </div>
      <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
        {expired ? 'El enlace expiró' : status === 'error' ? 'Error de conexión' : 'Enlace inválido'}
      </h1>
      <p className="text-brand-black/60 mb-8">
        {expired
          ? 'Por seguridad los enlaces para entrar duran 30 minutos. Pide uno nuevo.'
          : status === 'error'
            ? 'No pudimos validar el enlace. Revisa tu conexión e inténtalo de nuevo.'
            : 'Este enlace ya se usó o no es válido. Pide uno nuevo; siempre funciona el más reciente.'}
      </p>
      <Button variant="primary" onClick={() => navigate('/login')}>
        Pedir un enlace nuevo
      </Button>
    </div>
  );
};

export default LoginLink;
